import { type AtomEffect, atom, atomFamily } from "recoil";

export type AiChatLevel = 1 | 2 | 3;

const chatLevelStorageEffect: AtomEffect<AiChatLevel> = ({ setSelf, onSet }) => {
	const key = "ai_chat_level";
	const savedValue = localStorage.getItem(key);
	if (savedValue) {
		const parsed = Number.parseInt(savedValue, 10);
		if (parsed === 1 || parsed === 2 || parsed === 3) {
			setSelf(parsed);
		}
	}

	onSet((newValue, _, isReset) => {
		isReset
			? localStorage.removeItem(key)
			: localStorage.setItem(key, String(newValue));
	});
};

export interface ChatMessage {
	id: string;
	role: "user" | "assistant" | "human";
	content: string;
	timestamp: number;
	jadle?: string;
	isStreaming?: boolean;
}

export interface InlineEditTarget {
	lineNumber: number;
	endLineNumber: number;
	pageIndex: number;
	elementType: string;
	jadleSnippet: string;
}

/** Which editing level is active: 1=Chat, 2=Click-to-Edit, 3=Full Editor */
export const AiChatLevelAtom = atom<AiChatLevel>({
	key: "AiChatLevel",
	default: 1,
	effects: [chatLevelStorageEffect],
});

/** Whether the chat panel overlay is open */
export const AiChatPanelOpenAtom = atom<boolean>({
	key: "AiChatPanelOpen",
	default: false,
});

/** Chat conversation history per stack */
export const AiChatHistoryAtom = atomFamily<ChatMessage[], string>({
	key: "AiChatHistory",
	default: [],
});

/** Whether the AI is currently streaming a response */
export const AiIsStreamingAtom = atom<boolean>({
	key: "AiIsStreaming",
	default: false,
});

/** Level 2: which element is being edited inline */
export const InlineEditTargetAtom = atom<InlineEditTarget | null>({
	key: "InlineEditTarget",
	default: null,
});

/** Image generation progress queue for GENERATE: placeholders */
export type ImageStatus = "pending" | "generating" | "done" | "failed";
export const AiImageQueueAtom = atom<ImageStatus[]>({
	key: "AiImageQueue",
	default: [],
});

/** Callbacks for retry/continue when image generation has failures */
export const AiImageRetryAtom = atom<{
	retry: () => void;
	continueAnyway: () => void;
} | null>({
	key: "AiImageRetry",
	default: null,
});

/** A single image search result from a public domain source */
export interface PublicDomainImageOption {
	thumbnailUrl: string;
	fullImageUrl: string;
	title: string;
	source: string;
	sourcePageUrl: string;
	license: string;
}

/** Search results + selection state for one GENERATE: placeholder */
export interface ImagePickerSlot {
	prompt: string;
	results: PublicDomainImageOption[];
	status: "searching" | "ready" | "importing" | "done" | "failed";
	/** slide text around the placeholder, for the picker's context panel */
	context?: { before: string[]; after: string[] };
}

/** An image generated inside the picker — already persisted by the backend */
export interface GeneratedImage {
	generatedName: string;
	fileId: string;
	url: string;
	prompt: string;
}

/**
 * One answer per placeholder: a public-domain image to import, an image
 * already generated in the picker, a generation left for the legacy
 * no-picker path (generatePrompt), or nothing.
 */
export type ImagePickerSelection =
	| PublicDomainImageOption
	| { generatedName: string }
	| { generatePrompt: string }
	| "skip";

/** Image picker state: one slot per GENERATE: placeholder, or null when inactive */
export const AiImagePickerAtom = atom<ImagePickerSlot[] | null>({
	key: "AiImagePicker",
	default: null,
});

/** Callback invoked when the user makes selections in the image picker */
export const AiImagePickerResolveAtom = atom<((selections: ImagePickerSelection[]) => void) | null>({
	key: "AiImagePickerResolve",
	default: null,
});

/**
 * In-dialog generation, provided by useAiStream (which owns the auth/stack
 * context): the picker calls it to generate refinement candidates while
 * the dialog stays open. Null resolves as a failed generation.
 */
export const AiImagePickerGenerateAtom = atom<
	((prompt: string) => Promise<GeneratedImage | null>) | null
>({
	key: "AiImagePickerGenerate",
	default: null,
});

/** Deletes generated-but-unused images when the user chooses to discard them */
export const AiImagePickerDiscardAtom = atom<
	((images: GeneratedImage[]) => Promise<void>) | null
>({
	key: "AiImagePickerDiscard",
	default: null,
});
